
/**
 * Initialize performance monitoring - runs in both dev and prod for LCP optimization
 */
export function initPerformanceMonitoring() {
  try {
    console.log('🔍 Performance monitoring initialized');
      
    // Monitor React render performance
    const reportRenderMetrics = () => {
      try {
        if (performance && 'getEntriesByType' in performance) {
          const paintMetrics = performance.getEntriesByType('paint');
          
          paintMetrics.forEach(metric => {
            console.log(`🎨 ${metric.name}: ${metric.startTime.toFixed(2)}ms`);
          });
          
          // Add LCP monitoring
          const lcpObserver = new PerformanceObserver((entryList) => {
            const lcpEntries = entryList.getEntries();
            if (lcpEntries.length > 0) {
              const lcp = lcpEntries[lcpEntries.length - 1];
              console.log(`🚀 Largest Contentful Paint: ${lcp.startTime.toFixed(2)}ms`);
              
              // Report LCP to console in a visible way
              if (lcp.startTime > 2500) {
                console.warn(`⚠️ LCP is slow: ${lcp.startTime.toFixed(2)}ms (target: <2500ms)`);
                // Find LCP element and log it
                // @ts-ignore - TS doesn't know about the element property
                const lcpElement = lcp.element;
                if (lcpElement) {
                  console.warn('LCP element:', lcpElement);
                  console.warn('LCP element type:', lcpElement.tagName);
                  if (lcpElement.tagName === 'IMG') {
                    console.warn('LCP image src:', lcpElement.src);
                  }
                }
              } else {
                console.log(`✅ LCP is good: ${lcp.startTime.toFixed(2)}ms`);
              }
            }
          });
          
          lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

          // Add CLS monitoring
          const clsObserver = new PerformanceObserver((entryList) => {
            let clsValue = 0;
            
            // Properly type the LayoutShift entries
            interface LayoutShift extends PerformanceEntry {
              value: number;
              hadRecentInput: boolean;
            }
            
            for (const entry of entryList.getEntries() as LayoutShift[]) {
              if (!entry.hadRecentInput) {
                clsValue += entry.value;
              }
            }
            console.log(`📏 Cumulative Layout Shift: ${clsValue.toFixed(4)}`);
          });
          
          clsObserver.observe({ type: 'layout-shift', buffered: true });
          
          // Add FID monitoring
          const fidObserver = new PerformanceObserver((entryList) => {
            const firstInput = entryList.getEntries()[0];
            if (firstInput) {
              // Properly type FirstInputDelay
              interface FirstInputEvent extends PerformanceEntry {
                processingStart?: number;
                startTime: number;
              }
              
              const entry = firstInput as FirstInputEvent;
              if (entry.processingStart && entry.startTime) {
                const delay = entry.processingStart - entry.startTime;
                console.log(`👆 First Input Delay: ${delay.toFixed(2)}ms`);
              }
            }
          });
          
          fidObserver.observe({ type: 'first-input', buffered: true });
        }
      } catch (error) {
        console.error('Error reporting render metrics:', error);
      }
    };
    
    // Detect potential layout shifts early
    const detectEarlyLayoutShifts = () => {
      const images = document.querySelectorAll('img');
      images.forEach(img => {
        if (!img.hasAttribute('width') || !img.hasAttribute('height')) {
          console.warn('⚠️ Image without dimensions may cause layout shifts:', img);
        }
      });
    };
    
    // Report metrics after page load
    window.addEventListener('load', () => {
      // Use requestIdleCallback to not block the main thread
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(() => {
          reportRenderMetrics();
          detectEarlyLayoutShifts();
        });
      } else {
        setTimeout(() => {
          reportRenderMetrics();
          detectEarlyLayoutShifts();
        }, 200);
      }
    });
    
    // Track resources that might delay LCP
    const resourceObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        // Look for slow-loading resources that might delay LCP
        if (entry.duration > 1000) {
          console.warn(`⚠️ Slow resource load: ${entry.name} (${entry.duration.toFixed(2)}ms)`);
        }
      });
    });
    
    resourceObserver.observe({ entryTypes: ['resource'] });
    
    // Diagnose slow image loading
    document.addEventListener('DOMContentLoaded', () => {
      const images = document.querySelectorAll('img');
      images.forEach(img => {
        if (!img.complete) {
          img.addEventListener('load', () => {
            const loadTime = performance.now();
            console.log(`Image loaded: ${img.src} at ${loadTime.toFixed(2)}ms`);
          });
        }
      });
    });
  } catch (error) {
    console.error('Failed to initialize performance monitoring:', error);
  }
}

// Helper function to track when specific elements are loaded
export function trackElementTimings(selector: string, name: string) {
  try {
    const element = document.querySelector(selector);
    if (!element) {
      console.warn(`Element not found for timing: ${selector}`);
      return;
    }
    
    // Use Performance Observer if Element Timing API is available
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          console.log(`${name} timing: ${entry.startTime.toFixed(2)}ms`);
        });
      });
      
      // Try to use element timing if available
      try {
        observer.observe({ entryTypes: ['element'] });
        (element as any).elementTiming = name;
      } catch (e) {
        // Fallback to manual timing
        const start = performance.now();
        if (element instanceof HTMLImageElement) {
          if (element.complete) {
            console.log(`${name} already loaded at ${start.toFixed(2)}ms`);
          } else {
            element.addEventListener('load', () => {
              const end = performance.now();
              console.log(`${name} loaded in ${(end - start).toFixed(2)}ms`);
            });
          }
        }
      }
    }
  } catch (error) {
    console.error(`Error tracking element timing for ${name}:`, error);
  }
}
